/**
 * Thumbnail Cache Service
 * Handles fetching thumbnails via proxy endpoint with concurrency limiting and caching
 */

// Concurrency limiter
class Semaphore {
  private available: number;
  private queue: Array<() => void> = [];

  constructor(max: number) {
    this.available = max;
  }

  async acquire(): Promise<void> {
    if (this.available > 0) {
      this.available--;
      return;
    }
    await new Promise<void>((resolve) => this.queue.push(resolve));
    this.available--;
  }

  release(): void {
    this.available++;
    const next = this.queue.shift();
    if (next) next();
  }
}

// Thumbnail cache entry
interface ThumbCacheEntry {
  url: string;
  lastUsed: number;
}

// Constants
const THUMB_TIMEOUT_MS = 4000;
const MAX_THUMBS = 500;
const THUMB_SEMAPHORE = new Semaphore(6); // Max 6 concurrent thumbnail fetches

// Cache storage
const thumbUrlCache = new Map<string, ThumbCacheEntry>();

/**
 * Clamp a number between min and max
 */
function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Fetch with timeout
 */
async function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(input, { ...init, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

/**
 * Generate cache key for thumbnail
 */
function makeThumbKey(fileId: string, width: number): string {
  return `${fileId}:${width}`;
}

/**
 * Evict old cache entries if needed
 */
function evictIfNeeded(): void {
  if (thumbUrlCache.size <= MAX_THUMBS) return;
  
  // Evict LRU entries (10% of cache)
  const entries = [...thumbUrlCache.entries()].sort(
    (a, b) => a[1].lastUsed - b[1].lastUsed
  );
  const toEvict = entries.slice(0, Math.ceil(MAX_THUMBS * 0.1));
  
  for (const [key, entry] of toEvict) {
    URL.revokeObjectURL(entry.url);
    thumbUrlCache.delete(key);
  }
}

/**
 * Fetch thumbnail blob from proxy endpoint
 */
async function fetchThumbnailBlob(
  fileId: string,
  width: number,
  accessToken: string
): Promise<Blob> {
  const clampedWidth = clamp(Math.floor(width), 64, 2048);
  const url = `/api/drive/thumbnail?fileId=${encodeURIComponent(fileId)}&width=${clampedWidth}`;

  const res = await fetchWithTimeout(
    url,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    },
    THUMB_TIMEOUT_MS
  );

  if (!res.ok) {
    // One small fallback retry (max one retry, not 5)
    if (clampedWidth > 256) {
      const smaller = Math.max(256, Math.floor(clampedWidth * 0.66));
      const url2 = `/api/drive/thumbnail?fileId=${encodeURIComponent(fileId)}&width=${smaller}`;
      const res2 = await fetchWithTimeout(
        url2,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
        THUMB_TIMEOUT_MS
      );
      
      if (!res2.ok) {
        throw new Error(`Thumbnail proxy failed: ${res2.status}`);
      }
      return await res2.blob();
    }
    throw new Error(`Thumbnail proxy failed: ${res.status}`);
  }

  return await res.blob();
}

/**
 * Get thumbnail URL with concurrency limiting and caching
 */
export async function getThumbnailUrl(
  fileId: string,
  width: number,
  accessToken: string
): Promise<string> {
  const key = makeThumbKey(fileId, width);
  const cached = thumbUrlCache.get(key);
  
  if (cached) {
    cached.lastUsed = Date.now();
    return cached.url;
  }

  // Acquire semaphore to limit concurrent fetches
  await THUMB_SEMAPHORE.acquire();
  
  try {
    const blob = await fetchThumbnailBlob(fileId, width, accessToken);
    const url = URL.createObjectURL(blob);
    
    thumbUrlCache.set(key, { url, lastUsed: Date.now() });
    evictIfNeeded();
    
    return url;
  } finally {
    THUMB_SEMAPHORE.release();
  }
}

/**
 * Calculate optimal thumbnail width based on layout and device pixel ratio
 */
export function calculateThumbnailWidth(
  containerPx: number,
  isDetailView: boolean = false
): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  
  if (isDetailView) {
    // Detail view: use viewport width with DPR
    return clamp(Math.ceil(containerPx * dpr), 512, 2048);
  } else {
    // Grid view: use tile size with DPR
    return clamp(Math.ceil(containerPx * dpr), 256, 1024);
  }
}

/**
 * Clear thumbnail cache (useful for cleanup)
 */
export function clearThumbnailCache(): void {
  for (const [, entry] of thumbUrlCache.entries()) {
    URL.revokeObjectURL(entry.url);
  }
  thumbUrlCache.clear();
}
